import type { Adventure } from '../../types'
import { countingApplesAdventure, tillyCorrectReactions, tillyWrongReactions, tillyCelebrationReactions } from './countingApples'
import { numberFunAdventure } from './numberFun'
import { additionStarterAdventure } from './additionStarter'
import { patternFunAdventure } from './patternFun'
import { additionExplorerAdventure, tillyAdditionCorrect, tillyAdditionWrong } from './additionExplorer'
import { subtractionTalesAdventure, tillySubtractionCorrect, tillySubtractionWrong } from './subtractionTales'
import { sumsToTenAdventure, tillyTenCorrect, tillyTenWrong } from './sumsToTen'
import { mathMixUpAdventure, tillyMixCorrect, tillyMixWrong } from './mathMixUp'

// New adventures - March 2026
import { shapeStackerAdventure, tillyShapeCorrect, tillyShapeWrong } from './shapeStacker'
import { moreOrLessAdventure, tillyCompareCorrect, tillyCompareWrong } from './moreOrLess'
import { pirateTreasureAdventure, tillyPirateCorrect, tillyPirateWrong } from './pirateTreasure'
import { shapePatternsAdventure, tillyShapePatternCorrect, tillyShapePatternWrong } from './shapePatterns'
import { ordinalNumbersAdventure, tillyOrdinalCorrect, tillyOrdinalWrong } from './ordinalNumbers'
import { bigSmallAdventure, tillyMeasureCorrect, tillyMeasureWrong } from './bigSmall'
import { skipCountingAdventure, tillySkipCorrect, tillySkipWrong } from './skipCounting'
import { numberNeighborsAdventure, tillyNeighborsCorrect, tillyNeighborsWrong } from './numberNeighbors'
import { shapeDetectiveAdventure, tillyDetectiveCorrect, tillyDetectiveWrong } from './shapeDetective'
import { subtractionSeaAdventure, tillySeaCorrect, tillySeaWrong } from './subtractionSea'
import { towerBuilderAdventure, tillyTowerCorrect, tillyTowerWrong } from './towerBuilder'

// New adventures - March 29 2026 - Testing TTS workflow
import { countingAnimalsAdventure, tillyFarmCorrect, tillyFarmWrong } from './countingAnimals'
import { addingBalloonsAdventure, tillyBalloonCorrect, tillyBalloonWrong } from './addingBalloons'
import { cookieSharingAdventure, tillyCookieCorrect, tillyCookieWrong } from './cookieSharing'
import { shapeMagicAdventure, tillyShapeMagicCorrect, tillyShapeMagicWrong } from './shapeMagic'
import { numberRainbowAdventure, tillyRainbowCorrect, tillyRainbowWrong } from './numberRainbow'

// Export all adventures
export const allAdventures: Adventure[] = [
  countingApplesAdventure,
  numberFunAdventure,
  additionStarterAdventure,
  patternFunAdventure,
  additionExplorerAdventure,
  subtractionTalesAdventure,
  sumsToTenAdventure,
  mathMixUpAdventure,
  // New adventures - March 2026
  shapeStackerAdventure,
  moreOrLessAdventure,
  pirateTreasureAdventure,
  shapePatternsAdventure,
  ordinalNumbersAdventure,
  bigSmallAdventure,
  skipCountingAdventure,
  numberNeighborsAdventure,
  shapeDetectiveAdventure,
  subtractionSeaAdventure,
  towerBuilderAdventure,
  // Testing TTS workflow - March 29 2026
  countingAnimalsAdventure,
  addingBalloonsAdventure,
  cookieSharingAdventure,
  shapeMagicAdventure,
  numberRainbowAdventure,
]

// Get adventures filtered by age
export function getAdventuresByAge(age: '3' | '4' | '5'): Adventure[] {
  return allAdventures.filter(adventure => adventure.targetAge === age)
}

// Get a specific adventure by ID
export function getAdventureById(id: string): Adventure | undefined {
  return allAdventures.find(adventure => adventure.id === id)
}

// Get all addition-focused adventures
export function getAdditionAdventures(): Adventure[] {
  return allAdventures.filter(a => 
    a.theme === 'addition' || 
    a.id === 'addition-starter' ||
    a.id === 'addition-explorer' ||
    a.id === 'sums-to-ten' ||
    a.id === 'math-mix-up'
  )
}

// Get all subtraction-focused adventures  
export function getSubtractionAdventures(): Adventure[] {
  return allAdventures.filter(a => 
    a.id === 'take-away-tales' ||
    a.id === 'math-mix-up' ||
    a.id === 'pirate-treasure' ||
    a.id === 'subtraction-sea'
  )
}

// Get all shape-focused adventures
export function getShapeAdventures(): Adventure[] {
  return allAdventures.filter(a => 
    a.id === 'shape-stacker' ||
    a.id === 'shape-patterns' ||
    a.id === 'shape-detective' ||
    a.id === 'tower-builder'
  )
}

// Re-export Tilly reactions for use in components
export { 
  tillyCorrectReactions, 
  tillyWrongReactions, 
  tillyCelebrationReactions,
  tillyAdditionCorrect,
  tillyAdditionWrong,
  tillySubtractionCorrect,
  tillySubtractionWrong,
  tillyTenCorrect,
  tillyTenWrong,
  tillyMixCorrect,
  tillyMixWrong,
  // New reaction sets - March 2026
  tillyShapeCorrect,
  tillyShapeWrong,
  tillyCompareCorrect,
  tillyCompareWrong,
  tillyPirateCorrect,
  tillyPirateWrong,
  tillyShapePatternCorrect,
  tillyShapePatternWrong,
  tillyOrdinalCorrect,
  tillyOrdinalWrong,
  tillyMeasureCorrect,
  tillyMeasureWrong,
  tillySkipCorrect,
  tillySkipWrong,
  tillyNeighborsCorrect,
  tillyNeighborsWrong,
  tillyDetectiveCorrect,
  tillyDetectiveWrong,
  tillySeaCorrect,
  tillySeaWrong,
  tillyTowerCorrect,
  tillyTowerWrong,
  // New reaction sets - March 29 2026
  tillyFarmCorrect,
  tillyFarmWrong,
  tillyBalloonCorrect,
  tillyBalloonWrong,
  tillyCookieCorrect,
  tillyCookieWrong,
  tillyShapeMagicCorrect,
  tillyShapeMagicWrong,
  tillyRainbowCorrect,
  tillyRainbowWrong,
}

// Helper to get random reaction
export function getRandomReaction(reactions: string[]): string {
  return reactions[Math.floor(Math.random() * reactions.length)]
}

// Get themed reactions for an adventure
export function getThemedReactions(adventureId: string): { correct: string[], wrong: string[] } {
  switch (adventureId) {
    case 'addition-explorer':
      return { correct: tillyAdditionCorrect, wrong: tillyAdditionWrong }
    case 'take-away-tales':
      return { correct: tillySubtractionCorrect, wrong: tillySubtractionWrong }
    case 'sums-to-ten':
      return { correct: tillyTenCorrect, wrong: tillyTenWrong }
    case 'math-mix-up':
      return { correct: tillyMixCorrect, wrong: tillyMixWrong }
    // New adventures - March 2026
    case 'shape-stacker':
      return { correct: tillyShapeCorrect, wrong: tillyShapeWrong }
    case 'more-or-less':
      return { correct: tillyCompareCorrect, wrong: tillyCompareWrong }
    case 'pirate-treasure':
      return { correct: tillyPirateCorrect, wrong: tillyPirateWrong }
    case 'shape-patterns':
      return { correct: tillyShapePatternCorrect, wrong: tillyShapePatternWrong }
    case 'ordinal-numbers':
      return { correct: tillyOrdinalCorrect, wrong: tillyOrdinalWrong }
    case 'big-small':
      return { correct: tillyMeasureCorrect, wrong: tillyMeasureWrong }
    case 'skip-counting':
      return { correct: tillySkipCorrect, wrong: tillySkipWrong }
    case 'number-neighbors':
      return { correct: tillyNeighborsCorrect, wrong: tillyNeighborsWrong }
    case 'shape-detective':
      return { correct: tillyDetectiveCorrect, wrong: tillyDetectiveWrong }
    case 'subtraction-sea':
      return { correct: tillySeaCorrect, wrong: tillySeaWrong }
    case 'tower-builder':
      return { correct: tillyTowerCorrect, wrong: tillyTowerWrong }
    // Testing TTS workflow - March 29 2026
    case 'counting-animals':
      return { correct: tillyFarmCorrect, wrong: tillyFarmWrong }
    case 'adding-balloons':
      return { correct: tillyBalloonCorrect, wrong: tillyBalloonWrong }
    case 'cookie-sharing':
      return { correct: tillyCookieCorrect, wrong: tillyCookieWrong }
    case 'shape-magic':
      return { correct: tillyShapeMagicCorrect, wrong: tillyShapeMagicWrong }
    case 'number-rainbow':
      return { correct: tillyRainbowCorrect, wrong: tillyRainbowWrong }
    default:
      return { correct: tillyCorrectReactions, wrong: tillyWrongReactions }
  }
}
